// Reusable start/stop timer component for time tracking.
// Can be placed anywhere a matter context is available (Tasks, BillingTab, etc.)
// Creates a time entry with duration=0 on start, then backend tracks elapsed time.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import type { TimeEntry } from '../../billing/types/billing.types';

export interface TimeTrackerProps {
  /** Currently active timer entry (running or paused) for this matter, from useMatterBilling */
  activeTimer: TimeEntry | null;
  /** Create a time entry and start the timer */
  onStart: (dto: {
    entry_date: string;
    description: string;
    rate?: number;
    activity_category?: string;
    utbms_code?: string;
    entry_type?: 'hourly' | 'flat_rate';
    is_non_billable?: boolean;
  }) => Promise<TimeEntry>;
  /** Pause a running timer */
  onPause: (timeEntryId: string) => Promise<TimeEntry>;
  /** Resume a paused timer */
  onResume: (timeEntryId: string) => Promise<TimeEntry>;
  /** Stop and finalize the timer */
  onStop: (timeEntryId: string) => Promise<TimeEntry>;
  /** Called after the timer is stopped with the final time entry */
  onComplete?: (entry: TimeEntry) => void;
  /** Pre-fill description (e.g. task title) */
  defaultDescription?: string;
  /** Pre-fill activity category (e.g. task billing code) */
  defaultActivityCategory?: string;
  /** Pre-fill UTBMS code (e.g. L110 from task billing code) */
  defaultUtbmsCode?: string;
  /** Pre-fill rate */
  defaultRate?: number;
  /** Compact mode for inline placement */
  compact?: boolean;
}

function formatElapsed(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

const TimeTracker: React.FC<TimeTrackerProps> = ({
  activeTimer,
  onStart,
  onPause,
  onResume,
  onStop,
  onComplete,
  defaultDescription = '',
  defaultActivityCategory,
  defaultUtbmsCode,
  defaultRate,
  compact = false,
}) => {
  const [showStartForm, setShowStartForm] = useState(false);
  const [description, setDescription] = useState(defaultDescription);
  const [rate, setRate] = useState(defaultRate?.toString() || '');
  const [starting, setStarting] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Keep description in sync with prop changes (e.g. when task changes)
  const prevDescRef = useRef(defaultDescription);
  useEffect(() => {
    if (defaultDescription !== prevDescRef.current) {
      setDescription(defaultDescription);
      prevDescRef.current = defaultDescription;
    }
  }, [defaultDescription]);

  // Calculate elapsed time from active timer
  const calculateElapsed = useCallback(() => {
    if (!activeTimer) return 0;
    // Base accumulated duration from previous pause/resume sessions (in hours → seconds)
    const baseDuration = Number(activeTimer.duration || 0) * 3600;
    if (activeTimer.timer_status === 'paused') {
      return baseDuration;
    }
    if (activeTimer.timer_status === 'running' && activeTimer.timer_started_at) {
      const startedAt = new Date(activeTimer.timer_started_at).getTime();
      const now = Date.now();
      const currentSession = (now - startedAt) / 1000;
      return baseDuration + currentSession;
    }
    return baseDuration;
  }, [activeTimer]);

  // Set up interval for live clock when running
  useEffect(() => {
    if (activeTimer?.timer_status === 'running') {
      setElapsed(calculateElapsed());
      intervalRef.current = setInterval(() => {
        setElapsed(calculateElapsed());
      }, 1000);
    } else if (activeTimer?.timer_status === 'paused') {
      setElapsed(calculateElapsed());
      if (intervalRef.current) clearInterval(intervalRef.current);
    } else {
      setElapsed(0);
      if (intervalRef.current) clearInterval(intervalRef.current);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [activeTimer?.timer_status, activeTimer?.timer_started_at, calculateElapsed]);

  const handleStart = async () => {
    if (!description.trim()) {
      setError('Description is required');
      return;
    }
    try {
      setStarting(true);
      setError(null);
      await onStart({
        entry_date: new Date().toISOString().split('T')[0],
        description: description.trim(),
        rate: rate ? parseFloat(rate) : undefined,
        activity_category: defaultActivityCategory || undefined,
        utbms_code: defaultUtbmsCode || undefined,
        entry_type: 'hourly',
        is_non_billable: false,
      });
      setShowStartForm(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start timer');
    } finally {
      setStarting(false);
    }
  };

  const handlePause = async () => {
    if (!activeTimer) return;
    try {
      setActionLoading(true);
      setError(null);
      await onPause(activeTimer.time_entry_id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to pause timer');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResume = async () => {
    if (!activeTimer) return;
    try {
      setActionLoading(true);
      setError(null);
      await onResume(activeTimer.time_entry_id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resume timer');
    } finally {
      setActionLoading(false);
    }
  };

  const handleStop = async () => {
    if (!activeTimer) return;
    try {
      setActionLoading(true);
      setError(null);
      const finalEntry = await onStop(activeTimer.time_entry_id);
      onComplete?.(finalEntry);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to stop timer');
    } finally {
      setActionLoading(false);
    }
  };

  const isRunning = activeTimer?.timer_status === 'running';
  const isPaused = activeTimer?.timer_status === 'paused';
  const hasActiveTimer = isRunning || isPaused;

  // ── Active timer display ──
  if (hasActiveTimer) {
    if (compact) {
      return (
        <div className="flex items-center gap-2">
          <div className={`font-mono text-sm font-bold tabular-nums ${isRunning ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}`}>
            {formatElapsed(elapsed)}
          </div>
          {isRunning ? (
            <button
              onClick={handlePause}
              disabled={actionLoading}
              className="p-1 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded transition-colors disabled:opacity-50"
              title="Pause timer"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
              </svg>
            </button>
          ) : (
            <button
              onClick={handleResume}
              disabled={actionLoading}
              className="p-1 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded transition-colors disabled:opacity-50"
              title="Resume timer"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            </button>
          )}
          <button
            onClick={handleStop}
            disabled={actionLoading}
            className="p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors disabled:opacity-50"
            title="Stop timer"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M6 6h12v12H6z" />
            </svg>
          </button>
          {error && <span className="text-xs text-red-500">{error}</span>}
        </div>
      );
    }

    return (
      <div className="flex items-center gap-3 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        {/* Pulsing indicator */}
        <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${isRunning ? 'bg-green-500 animate-pulse' : 'bg-amber-500'}`} />

        {/* Timer display */}
        <div className="flex-1 min-w-0">
          <div className={`font-mono text-lg font-bold tabular-nums ${isRunning ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}`}>
            {formatElapsed(elapsed)}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
            {activeTimer?.description || 'Timer running'}
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-1.5">
          {isRunning ? (
            <button
              onClick={handlePause}
              disabled={actionLoading}
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-900/20 rounded-lg hover:bg-amber-200 dark:hover:bg-amber-900/40 transition-colors disabled:opacity-50"
            >
              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
              </svg>
              Pause
            </button>
          ) : (
            <button
              onClick={handleResume}
              disabled={actionLoading}
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-900/20 rounded-lg hover:bg-green-200 dark:hover:bg-green-900/40 transition-colors disabled:opacity-50"
            >
              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
              Resume
            </button>
          )}
          <button
            onClick={handleStop}
            disabled={actionLoading}
            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/20 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/40 transition-colors disabled:opacity-50"
          >
            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M6 6h12v12H6z" />
            </svg>
            Stop
          </button>
        </div>
        {error && <span className="text-xs text-red-500 ml-2">{error}</span>}
      </div>
    );
  }

  // ── No active timer: show start button / form ──

  if (showStartForm) {
    return (
      <div className={`rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 ${compact ? 'p-2' : 'p-3'}`}>
        <div className={`grid gap-2 ${compact ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-1 sm:grid-cols-4'}`}>
          <div className={compact ? '' : 'sm:col-span-2'}>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What are you working on?"
              className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter') handleStart(); }}
            />
          </div>
          <div>
            <input
              type="number"
              min="0"
              step="1"
              value={rate}
              onChange={(e) => setRate(e.target.value)}
              placeholder="Rate ($/hr)"
              className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
          <div className="flex gap-1.5">
            <button
              onClick={handleStart}
              disabled={starting || !description.trim()}
              className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
              {starting ? 'Starting...' : 'Start'}
            </button>
            <button
              onClick={() => { setShowStartForm(false); setError(null); }}
              className="px-2.5 py-1.5 text-sm text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
        {error && <p className="text-xs text-red-500 mt-1.5">{error}</p>}
      </div>
    );
  }

  return (
    <button
      onClick={() => {
        setDescription(defaultDescription);
        setRate(defaultRate?.toString() || '');
        setShowStartForm(true);
      }}
      className={`flex items-center gap-1.5 font-medium rounded-lg transition-colors ${
        compact
          ? 'px-2.5 py-1 text-xs bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-900/40'
          : 'px-3 py-1.5 text-sm bg-green-600 text-white hover:bg-green-700'
      }`}
    >
      <svg className={compact ? 'w-3.5 h-3.5' : 'w-4 h-4'} fill="currentColor" viewBox="0 0 24 24">
        <path d="M8 5v14l11-7z" />
      </svg>
      Start Timer
    </button>
  );
};

export default TimeTracker;
